import { openThreadReplicaDb, requestToPromise, transactionToPromise } from "@/lib/thread-replica-idb"
import {
  areThreadReplicaEventRowsEquivalent,
  buildThreadReplicaBaselineEventRow,
  buildThreadReplicaEventSequenceRange,
  buildThreadReplicaReplayIdentityLookupKey,
  buildThreadReplicaRuntimeEventRow,
  getThreadReplicaEventsStoreName,
  getThreadReplicaReplayIdentityIndexName,
} from "@/lib/thread-replica-event-store"
import {
  buildEmptyThreadReplicaMeta,
  EMPTY_THREAD_REPLICA_CURSOR,
  FIRST_THREAD_REPLICA_SEQUENCE,
  INITIAL_THREAD_REPLICA_EPOCH,
  THREAD_REPLICA_CHECKPOINTS_STORE,
  THREAD_REPLICA_EVENT_CONTRACT_VERSION,
  THREAD_REPLICA_META_STORE,
  THREAD_REPLICA_PROJECTOR_VERSION,
  type ThreadReplicaAppendResult,
  type ThreadReplicaBaselineEventInput,
  type ThreadReplicaEventLane,
  type ThreadReplicaEventRow,
  type ThreadReplicaMetaRecord,
  type ThreadReplicaRuntimeEventInput,
  type ThreadReplicaScope,
  type ThreadReplicaServerResumeContract,
  normalizeThreadReplicaEventRow,
  normalizeThreadReplicaMetaRecord,
} from "@/lib/thread-replica-types"
import type { ThreadSyncReplicaSourceKind } from "@/lib/thread-sync-replica-provenance"

type ThreadReplicaAppendArgs = ThreadReplicaScope & {
  lane: "runtime"
  events: ThreadReplicaRuntimeEventInput[]
  expectedAfter: number
  expectedEpoch?: number
  serverResume?: ThreadReplicaServerResumeContract | null
  sourceKind: ThreadSyncReplicaSourceKind | null
  nowMs: number
} | ThreadReplicaScope & {
  lane: "baseline"
  events: ThreadReplicaBaselineEventInput[]
  expectedAfter: number
  expectedEpoch?: number
  serverResume?: null
  sourceKind: ThreadSyncReplicaSourceKind | null
  nowMs: number
}

function normalizeExpectedAfter(value: number): number {
  if (!Number.isFinite(value) || value < EMPTY_THREAD_REPLICA_CURSOR) {
    return EMPTY_THREAD_REPLICA_CURSOR
  }

  return Math.floor(value)
}

function getLaneTail(args: {
  meta: ThreadReplicaMetaRecord
  lane: ThreadReplicaEventLane
}): number {
  return args.lane === "runtime" ? args.meta.localCursor : args.meta.lastBaselineSequence
}

function getLaneCoverageForMeta(args: {
  lane: ThreadReplicaEventLane
  currentMeta: ThreadReplicaMetaRecord
  nextTail: number
}): Pick<ThreadReplicaMetaRecord, "coverage" | "baselineCoverage"> {
  if (args.lane === "runtime") {
    return {
      coverage:
        args.nextTail >= FIRST_THREAD_REPLICA_SEQUENCE
          ? args.currentMeta.coverage === "tail_window"
            ? "tail_window"
            : "full"
          : "none",
      baselineCoverage: "none",
    }
  }

  return {
    coverage: "none",
    baselineCoverage: args.nextTail >= FIRST_THREAD_REPLICA_SEQUENCE ? "complete" : "none",
  }
}

function buildConflictMeta(args: {
  currentMeta: ThreadReplicaMetaRecord
  nowMs: number
  reason: ThreadReplicaMetaRecord["lastResetReason"]
}): ThreadReplicaMetaRecord {
  return {
    ...buildEmptyThreadReplicaMeta({
      scopeKey: args.currentMeta.scopeKey,
      sessionId: args.currentMeta.sessionId,
      nowMs: args.nowMs,
    }),
    replicaEpoch: Math.max(INITIAL_THREAD_REPLICA_EPOCH, args.currentMeta.replicaEpoch + 1),
    eventContractVersion: THREAD_REPLICA_EVENT_CONTRACT_VERSION,
    projectorVersion: THREAD_REPLICA_PROJECTOR_VERSION,
    lastResetReason: args.reason,
  }
}

function buildEventRow(args: {
  scopeKey: string
  sessionId: string
  replicaEpoch: number
  lane: ThreadReplicaEventLane
  event: ThreadReplicaRuntimeEventInput | ThreadReplicaBaselineEventInput
  sourceKind: ThreadSyncReplicaSourceKind | null
}): ThreadReplicaEventRow {
  return args.lane === "runtime"
    ? buildThreadReplicaRuntimeEventRow({
        scopeKey: args.scopeKey,
        sessionId: args.sessionId,
        replicaEpoch: args.replicaEpoch,
        event: args.event as ThreadReplicaRuntimeEventInput,
        sourceKind: args.sourceKind,
      })
    : buildThreadReplicaBaselineEventRow({
        scopeKey: args.scopeKey,
        sessionId: args.sessionId,
        replicaEpoch: args.replicaEpoch,
        event: args.event as ThreadReplicaBaselineEventInput,
        sourceKind: args.sourceKind,
      })
}

function getEventSequence(row: ThreadReplicaEventRow): number {
  return row.sequence
}

function getIncomingContractVersion(events: ThreadReplicaAppendArgs["events"]): number | null {
  let contractVersion: number | null = null
  for (const event of events) {
    if (!Number.isFinite(event.contractVersion) || event.contractVersion < FIRST_THREAD_REPLICA_SEQUENCE) {
      return null
    }
    const normalizedContractVersion = Math.floor(event.contractVersion)
    if (contractVersion === null) {
      contractVersion = normalizedContractVersion
      continue
    }
    if (contractVersion !== normalizedContractVersion) {
      return null
    }
  }
  return contractVersion
}

async function clearReplicaRowsForSession(args: {
  metaStore: IDBObjectStore
  eventsStore: IDBObjectStore
  checkpointsStore: IDBObjectStore
  scopeKey: string
  sessionId: string
  currentMeta: ThreadReplicaMetaRecord
  nextMeta: ThreadReplicaMetaRecord
}): Promise<void> {
  args.eventsStore.delete(
    buildThreadReplicaEventSequenceRange({
      scopeKey: args.scopeKey,
      sessionId: args.sessionId,
      lane: "runtime",
      fromSequence: FIRST_THREAD_REPLICA_SEQUENCE,
      toSequence: Number.MAX_SAFE_INTEGER,
    }),
  )
  args.eventsStore.delete(
    buildThreadReplicaEventSequenceRange({
      scopeKey: args.scopeKey,
      sessionId: args.sessionId,
      lane: "baseline",
      fromSequence: FIRST_THREAD_REPLICA_SEQUENCE,
      toSequence: Number.MAX_SAFE_INTEGER,
    }),
  )

  args.checkpointsStore.delete(
    IDBKeyRange.bound(
      [args.scopeKey, args.sessionId, EMPTY_THREAD_REPLICA_CURSOR, EMPTY_THREAD_REPLICA_CURSOR],
      [
        args.scopeKey,
        args.sessionId,
        Math.max(args.currentMeta.replicaEpoch, args.nextMeta.replicaEpoch),
        Number.MAX_SAFE_INTEGER,
      ],
    ),
  )

  args.metaStore.put(args.nextMeta)
}

export async function appendThreadReplicaDelta(
  args: ThreadReplicaAppendArgs,
): Promise<ThreadReplicaAppendResult> {
  const db = await openThreadReplicaDb()
  if (!db) {
    return { status: "storage_unavailable" }
  }

  const transaction = db.transaction(
    [THREAD_REPLICA_META_STORE, getThreadReplicaEventsStoreName(), THREAD_REPLICA_CHECKPOINTS_STORE],
    "readwrite",
  )
  const metaStore = transaction.objectStore(THREAD_REPLICA_META_STORE)
  const eventsStore = transaction.objectStore(getThreadReplicaEventsStoreName())
  const checkpointsStore = transaction.objectStore(THREAD_REPLICA_CHECKPOINTS_STORE)

  const currentMeta =
    normalizeThreadReplicaMetaRecord(
      await requestToPromise(metaStore.get([args.scopeKey, args.sessionId])),
    ) ?? buildEmptyThreadReplicaMeta({ scopeKey: args.scopeKey, sessionId: args.sessionId, nowMs: args.nowMs })

  if (
    typeof args.expectedEpoch === "number" &&
    Number.isFinite(args.expectedEpoch) &&
    Math.floor(args.expectedEpoch) !== currentMeta.replicaEpoch
  ) {
    transaction.abort()
    return { status: "stale_writer", currentEpoch: currentMeta.replicaEpoch }
  }

  const incomingContractVersion = getIncomingContractVersion(args.events)
  if (args.events.length > 0 && incomingContractVersion === null) {
    const nextMeta = buildConflictMeta({
      currentMeta,
      nowMs: args.nowMs,
      reason: "event_contract_changed",
    })
    await clearReplicaRowsForSession({
      metaStore,
      eventsStore,
      checkpointsStore,
      scopeKey: args.scopeKey,
      sessionId: args.sessionId,
      currentMeta,
      nextMeta,
    })
    await transactionToPromise(transaction)
    return {
      status: "conflict",
      nextEpoch: nextMeta.replicaEpoch,
      reason: "event_contract_changed",
    }
  }
  const hasPersistedReplicaData =
    currentMeta.coverage !== "none" || currentMeta.baselineCoverage !== "none"
  if (
    incomingContractVersion !== null &&
    hasPersistedReplicaData &&
    currentMeta.eventContractVersion !== incomingContractVersion
  ) {
    const nextMeta = buildConflictMeta({
      currentMeta,
      nowMs: args.nowMs,
      reason: "event_contract_changed",
    })
    await clearReplicaRowsForSession({
      metaStore,
      eventsStore,
      checkpointsStore,
      scopeKey: args.scopeKey,
      sessionId: args.sessionId,
      currentMeta,
      nextMeta,
    })
    await transactionToPromise(transaction)
    return {
      status: "conflict",
      nextEpoch: nextMeta.replicaEpoch,
      reason: "event_contract_changed",
    }
  }

  const expectedAfter = normalizeExpectedAfter(args.expectedAfter)
  const currentTail = getLaneTail({ meta: currentMeta, lane: args.lane })
  const oldestExpectedAfter =
    args.lane === "runtime" &&
    currentMeta.coverage === "tail_window" &&
    currentMeta.oldestAvailableCursor >= FIRST_THREAD_REPLICA_SEQUENCE
      ? currentMeta.oldestAvailableCursor - 1
      : EMPTY_THREAD_REPLICA_CURSOR
  if (expectedAfter < oldestExpectedAfter) {
    transaction.abort()
    return {
      status: "gap",
      currentSequence: oldestExpectedAfter,
      expectedAfter,
      reason: "before_oldest_available",
    }
  }
  if (expectedAfter > currentTail) {
    transaction.abort()
    return {
      status: "gap",
      currentSequence: currentTail,
      expectedAfter,
      reason: "after_current_tail",
    }
  }

  let lastSeenSequence = expectedAfter
  let appendedCount = 0
  let duplicateCount = 0
  const rowsToPersist: ThreadReplicaEventRow[] = []
  const incomingReplayIdentityRows = new Map<string, ThreadReplicaEventRow>()
  const replayIdentityIndex = eventsStore.index(getThreadReplicaReplayIdentityIndexName())

  for (const event of args.events) {
    const row = buildEventRow({
      scopeKey: args.scopeKey,
      sessionId: args.sessionId,
      replicaEpoch: currentMeta.replicaEpoch,
      lane: args.lane,
      event,
      sourceKind: args.sourceKind,
    })
    const nextExpectedSequence = lastSeenSequence + 1
      if (getEventSequence(row) !== nextExpectedSequence) {
        transaction.abort()
        return {
          status: "gap",
          currentSequence: lastSeenSequence,
          expectedAfter: lastSeenSequence,
          reason: "non_contiguous_input",
        }
      }
    lastSeenSequence = row.sequence

    const existingIncomingRow = incomingReplayIdentityRows.get(row.replayEventId)
    if (existingIncomingRow) {
      const nextMeta = buildConflictMeta({
        currentMeta,
        nowMs: args.nowMs,
        reason: "replay_identity_conflict",
      })
      await clearReplicaRowsForSession({
        metaStore,
        eventsStore,
        checkpointsStore,
        scopeKey: args.scopeKey,
        sessionId: args.sessionId,
        currentMeta,
        nextMeta,
      })
      await transactionToPromise(transaction)
      return {
        status: "conflict",
        nextEpoch: nextMeta.replicaEpoch,
        reason: "replay_identity_conflict",
      }
    }
    incomingReplayIdentityRows.set(row.replayEventId, row)

    const existingRowByIdentity = normalizeThreadReplicaEventRow(
      await requestToPromise(
        replayIdentityIndex.get(
          buildThreadReplicaReplayIdentityLookupKey({
            scopeKey: args.scopeKey,
            sessionId: args.sessionId,
            lane: args.lane,
            replayEventId: row.replayEventId,
          }),
        ),
      ),
    )
    if (existingRowByIdentity && existingRowByIdentity.sequence !== row.sequence) {
      const nextMeta = buildConflictMeta({
        currentMeta,
        nowMs: args.nowMs,
        reason: "replay_identity_conflict",
      })
      await clearReplicaRowsForSession({
        metaStore,
        eventsStore,
        checkpointsStore,
        scopeKey: args.scopeKey,
        sessionId: args.sessionId,
        currentMeta,
        nextMeta,
      })
      await transactionToPromise(transaction)
      return {
        status: "conflict",
        nextEpoch: nextMeta.replicaEpoch,
        reason: "replay_identity_conflict",
      }
    }

    const existingRow = normalizeThreadReplicaEventRow(
      await requestToPromise(eventsStore.get([args.scopeKey, args.sessionId, args.lane, row.sequence])),
    )
    if (existingRow) {
      if (!areThreadReplicaEventRowsEquivalent({ left: existingRow, right: row })) {
        const nextMeta = buildConflictMeta({
          currentMeta,
          nowMs: args.nowMs,
          reason: "conflicting_overlap",
        })
        await clearReplicaRowsForSession({
          metaStore,
          eventsStore,
          checkpointsStore,
          scopeKey: args.scopeKey,
          sessionId: args.sessionId,
          currentMeta,
          nextMeta,
        })
        await transactionToPromise(transaction)
        return {
          status: "conflict",
          nextEpoch: nextMeta.replicaEpoch,
          reason: "conflicting_overlap",
        }
      }
      duplicateCount += 1
      continue
    }

    rowsToPersist.push(row)
    appendedCount += 1
  }

  for (const row of rowsToPersist) {
    eventsStore.put(row)
  }

  const nextTail = Math.max(currentTail, lastSeenSequence)
  const shouldPromoteRuntimeValidation =
    args.lane === "runtime" &&
    typeof args.serverResume?.historyToken === "string" &&
    args.serverResume.historyToken.trim().length > 0 &&
    Number.isFinite(args.serverResume.localCursor) &&
    Math.floor(args.serverResume.localCursor) === nextTail
  const nextMeta: ThreadReplicaMetaRecord = {
    ...currentMeta,
    eventContractVersion: incomingContractVersion ?? currentMeta.eventContractVersion,
    projectorVersion: THREAD_REPLICA_PROJECTOR_VERSION,
    historyToken:
      shouldPromoteRuntimeValidation
        ? args.serverResume?.historyToken?.trim() ?? currentMeta.historyToken
        : currentMeta.historyToken,
    localCursor: args.lane === "runtime" ? nextTail : currentMeta.localCursor,
    oldestAvailableCursor:
      args.lane === "runtime" && nextTail >= FIRST_THREAD_REPLICA_SEQUENCE
        ? currentMeta.oldestAvailableCursor >= FIRST_THREAD_REPLICA_SEQUENCE
          ? currentMeta.oldestAvailableCursor
          : FIRST_THREAD_REPLICA_SEQUENCE
        : currentMeta.oldestAvailableCursor,
    coverage:
      args.lane === "runtime"
        ? getLaneCoverageForMeta({ lane: "runtime", currentMeta, nextTail }).coverage
        : currentMeta.coverage,
    baselineCoverage:
      args.lane === "baseline"
        ? getLaneCoverageForMeta({ lane: "baseline", currentMeta, nextTail }).baselineCoverage
        : currentMeta.baselineCoverage,
    lastBaselineSequence: args.lane === "baseline" ? nextTail : currentMeta.lastBaselineSequence,
    lastUpdatedAtMs: Math.max(EMPTY_THREAD_REPLICA_CURSOR, Math.floor(args.nowMs)),
    lastValidatedAtMs: shouldPromoteRuntimeValidation
      ? Math.max(EMPTY_THREAD_REPLICA_CURSOR, Math.floor(args.nowMs))
      : currentMeta.lastValidatedAtMs,
    lastResetReason: null,
  }

  if (args.lane === "runtime" && nextMeta.localCursor === EMPTY_THREAD_REPLICA_CURSOR) {
    nextMeta.coverage = "none"
  }
  if (args.lane === "baseline" && nextMeta.lastBaselineSequence === EMPTY_THREAD_REPLICA_CURSOR) {
    nextMeta.baselineCoverage = "none"
  }

  metaStore.put(nextMeta)
  await transactionToPromise(transaction)
  return {
    status: appendedCount > 0 ? "appended" : "duplicate",
    meta: nextMeta,
    appendedCount,
    duplicateCount,
  }
}

export async function clearThreadReplicaSession(args: ThreadReplicaScope & {
  expectedEpoch?: number
  reason: ThreadReplicaMetaRecord["lastResetReason"]
  nowMs: number
}): Promise<{ status: "cleared"; meta: ThreadReplicaMetaRecord } | { status: "stale_writer" | "storage_unavailable" }> {
  const db = await openThreadReplicaDb()
  if (!db) {
    return { status: "storage_unavailable" }
  }

  const transaction = db.transaction(
    [THREAD_REPLICA_META_STORE, getThreadReplicaEventsStoreName(), THREAD_REPLICA_CHECKPOINTS_STORE],
    "readwrite",
  )
  const metaStore = transaction.objectStore(THREAD_REPLICA_META_STORE)
  const eventsStore = transaction.objectStore(getThreadReplicaEventsStoreName())
  const checkpointsStore = transaction.objectStore(THREAD_REPLICA_CHECKPOINTS_STORE)

  const currentMeta =
    normalizeThreadReplicaMetaRecord(
      await requestToPromise(metaStore.get([args.scopeKey, args.sessionId])),
    ) ?? buildEmptyThreadReplicaMeta({ scopeKey: args.scopeKey, sessionId: args.sessionId, nowMs: args.nowMs })

  if (
    typeof args.expectedEpoch === "number" &&
    Number.isFinite(args.expectedEpoch) &&
    Math.floor(args.expectedEpoch) !== currentMeta.replicaEpoch
  ) {
    transaction.abort()
    return { status: "stale_writer" }
  }

  const nextMeta = buildConflictMeta({
    currentMeta,
    nowMs: args.nowMs,
    reason: args.reason,
  })
  await clearReplicaRowsForSession({
    metaStore,
    eventsStore,
    checkpointsStore,
    scopeKey: args.scopeKey,
    sessionId: args.sessionId,
    currentMeta,
    nextMeta,
  })
  await transactionToPromise(transaction)
  return {
    status: "cleared",
    meta: nextMeta,
  }
}
